<template>
  <div class="meter-water-manage-container">
    <!-- (1) PageHeader -->
    <ds-page-header :title="$t('meterWater.meterReadingInfo')" :subtitle="$t('meterWater.page.subtitle')">
      <template #extra>
        <el-button v-if="conditions.objId" type="primary" icon="el-icon-edit-outline" @click="handleOpenAddMeter">
          {{ $t('meterWater.readMeter') }}
        </el-button>
        <el-button type="text" icon="el-icon-s-operation" @click="handleOpenMeterType">
          {{ $t('meterWater.meterType') }}
        </el-button>
        <el-button v-if="conditions.objId" type="text" icon="el-icon-camera" @click="handleOpenQrCode">
          {{ $t('meterWater.qrCodeMeter') }}
        </el-button>
        <el-button type="text" icon="el-icon-plus" @click="handleOpenImport1">
          {{ $t('meterWater.import1') }}
        </el-button>
        <el-button type="text" icon="el-icon-plus" @click="handleOpenImport2">
          {{ $t('meterWater.import2') }}
        </el-button>
      </template>
    </ds-page-header>

    <div class="meter-water-layout">
      <!-- 左侧房屋树 -->
      <div class="ds-panel ds-panel--tree">
        <room-tree-div ref="roomTree" @selectRoom="handleSelectRoom" />
      </div>

      <div class="meter-water-main">
        <!-- (2) SearchPanel -->
        <ds-search-panel>
          <el-row :gutter="16">
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-select v-model="conditions.meterType" clearable class="w-full"
                :placeholder="$t('meterWater.selectMeterType')">
                <el-option v-for="item in meterTypes" :key="item.typeId" :label="item.typeName" :value="item.typeId" />
              </el-select>
            </el-col>
            <el-col :xs="24" :sm="12" :md="8" :lg="6">
              <el-input v-model.trim="conditions.waterId" clearable class="w-full"
                :placeholder="$t('meterWater.inputMeterId')" @keyup.enter.native="handleQuery" />
            </el-col>
          </el-row>

          <template #actions>
            <el-button type="primary" icon="el-icon-search" @click="handleQuery">
              {{ $t('common.query') }}
            </el-button>
            <el-button icon="el-icon-refresh-left" @click="handleReset">
              {{ $t('common.reset') }}
            </el-button>
          </template>
        </ds-search-panel>

        <!-- (3)(4)(6) Toolbar + DataTable + PaginationBar -->
        <div class="ds-panel">
          <ds-toolbar :total="pagination.total" :density="density" :show-column-setting="false" :show-refresh="true"
            @update:density="density = $event" @refresh="loadData">
            <template #left>
              <span class="ds-panel__title">{{ roomName || $t('meterWater.meterReadingInfo') }}</span>
            </template>
          </ds-toolbar>

          <ds-data-table :data="meterWaters" :loading="loading" :error="hasError" :error-message="errorMessage"
            :density="density" border :empty-description="$t('meterWater.page.empty')" @retry="loadData">
            <el-table-column prop="waterId" :label="$t('meterWater.meterId')" align="center" width="140">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.waterId || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="meterTypeName" :label="$t('meterWater.meterType')" align="center" width="130">
              <template slot-scope="scope">
                {{ scope.row.meterTypeName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="objName" :label="$t('meterWater.objectName')" align="left" min-width="150"
              show-overflow-tooltip>
              <template slot-scope="scope">
                {{ scope.row.objName || '-' }}
              </template>
            </el-table-column>
            <el-table-column prop="preDegrees" :label="$t('meterWater.preDegrees')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.preDegrees == null ? '-' : scope.row.preDegrees }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="curDegrees" :label="$t('meterWater.curDegrees')" align="right" width="120">
              <template slot-scope="scope">
                <span class="ds-num">{{ scope.row.curDegrees == null ? '-' : scope.row.curDegrees }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="preReadingTime" :label="$t('meterWater.preReadingTime')" align="center" width="170">
              <template slot-scope="scope">
                {{ formatDateTimeSec(scope.row.preReadingTime) }}
              </template>
            </el-table-column>
            <el-table-column prop="curReadingTime" :label="$t('meterWater.curReadingTime')" align="center" width="170">
              <template slot-scope="scope">
                {{ formatDateTimeSec(scope.row.curReadingTime) }}
              </template>
            </el-table-column>
            <el-table-column prop="createTime" :label="$t('meterWater.createTime')" align="center" width="170">
              <template slot-scope="scope">
                {{ formatDateTimeSec(scope.row.createTime) }}
              </template>
            </el-table-column>
            <el-table-column :label="$t('common.operation')" align="center" width="140">
              <template slot-scope="scope">
                <el-button type="text" size="small" @click="handleEdit(scope.row)">
                  {{ $t('common.edit') }}
                </el-button>
                <el-button type="text" size="small" class="ds-btn-danger" @click="handleDelete(scope.row)">
                  {{ $t('common.delete') }}
                </el-button>
              </template>
            </el-table-column>
          </ds-data-table>

          <ds-pagination-bar :total="pagination.total" :page="pagination.current" :size="pagination.size"
            :page-sizes="[10, 20, 30, 50]" @size-change="handleSizeChange" @current-change="handleCurrentChange" />
        </div>
      </div>
    </div>

    <!-- 组件 -->
    <add-meter-water ref="addMeterWater" @success="handleSuccess" />
    <edit-meter-water ref="editMeterWater" @success="handleSuccess" />
    <delete-meter-water ref="deleteMeterWater" @success="handleSuccess" />
    <import-meter-water-fee ref="importMeterWaterFee" />
    <import-meter-water-fee2 ref="importMeterWaterFee2" />
    <room-meter-qrcode ref="roomMeterQrcode" />
  </div>
</template>

<script>
import {
  listMeterWaters,
  listMeterTypes
} from '@/api/fee/meterWaterManageApi'
import { getCommunityId } from '@/api/community/communityApi'
import RoomTreeDiv from '@/components/room/roomTreeDiv'
import AddMeterWater from '@/components/fee/addMeterWater'
import EditMeterWater from '@/components/fee/editMeterWater'
import DeleteMeterWater from '@/components/fee/deleteMeterWater'
import ImportMeterWaterFee from '@/components/fee/importMeterWaterFee'
import ImportMeterWaterFee2 from '@/components/fee/importMeterWaterFee2'
import RoomMeterQrcode from '@/components/fee/roomMeterQrcode'
import { formatDateTimeSec } from '@/utils/format'
import DsPageHeader from '@/components/ds/ds-page-header.vue'
import DsSearchPanel from '@/components/ds/ds-search-panel.vue'
import DsToolbar from '@/components/ds/ds-toolbar.vue'
import DsDataTable from '@/components/ds/ds-data-table.vue'
import DsPaginationBar from '@/components/ds/ds-pagination-bar.vue'

export default {
  name: 'MeterWaterManageList',
  components: {
    RoomTreeDiv,
    AddMeterWater,
    EditMeterWater,
    DeleteMeterWater,
    ImportMeterWaterFee,
    ImportMeterWaterFee2,
    RoomMeterQrcode,
    DsPageHeader,
    DsSearchPanel,
    DsToolbar,
    DsDataTable,
    DsPaginationBar
  },
  data() {
    return {
      loading: false,
      hasError: false,
      errorMessage: '',
      density: 'default',
      conditions: {
        waterId: '',
        meterType: '',
        roomNum: '',
        objId: '',
        page: 1,
        row: 10,
        communityId: ''
      },
      meterWaters: [],
      meterTypes: [],
      pagination: {
        current: 1,
        size: 10,
        total: 0
      },
      roomName: ''
    }
  },
  created() {
    this.conditions.communityId = getCommunityId()
    this.loadData()
    this.loadMeterTypes()
  },
  methods: {
    async loadData() {
      this.loading = true
      this.hasError = false
      this.errorMessage = ''
      try {
        const { data, total } = await listMeterWaters(this.conditions)
        this.meterWaters = Array.isArray(data) ? data : []
        this.pagination.total = total || 0
      } catch (error) {
        this.hasError = true
        this.errorMessage = this.$t('meterWater.page.loadFailed')
        console.error('Failed to load meter waters:', error)
      } finally {
        this.loading = false
      }
    },
    async loadMeterTypes() {
      try {
        const { data } = await listMeterTypes({
          communityId: this.conditions.communityId,
          page: 1,
          row: 100
        })
        this.meterTypes = Array.isArray(data) ? data : []
      } catch (error) {
        console.error('Failed to load meter types:', error)
      }
    },
    handleQuery() {
      this.conditions.page = 1
      this.loadData()
    },
    handleReset() {
      this.conditions.waterId = ''
      this.conditions.meterType = ''
      this.conditions.roomNum = ''
      this.handleQuery()
    },
    handleSizeChange(val) {
      this.conditions.row = val
      this.loadData()
    },
    handleCurrentChange(val) {
      this.conditions.page = val
      this.loadData()
    },
    handleSelectRoom({ roomId, roomName }) {
      this.conditions.objId = roomId
      this.roomName = roomName
      this.handleQuery()
    },
    handleOpenAddMeter() {
      this.$refs.addMeterWater.open({
        roomId: this.conditions.objId,
        roomName: this.roomName
      })
    },
    handleEdit(row) {
      this.$refs.editMeterWater.open(row)
    },
    handleDelete(row) {
      this.$refs.deleteMeterWater.open(row)
    },
    handleOpenImport1() {
      this.$refs.importMeterWaterFee.open()
    },
    handleOpenImport2() {
      this.$refs.importMeterWaterFee2.open()
    },
    handleOpenQrCode() {
      this.$refs.roomMeterQrcode.open({
        roomId: this.conditions.objId,
        roomName: this.roomName
      })
    },
    handleOpenMeterType() {
      this.$router.push('/views/fee/meterTypeManage')
    },
    handleSuccess() {
      this.loadData()
    },
    formatDateTimeSec(value) {
      return formatDateTimeSec(value)
    }
  }
}
</script>

<style lang="scss" scoped>
.meter-water-manage-container {
  padding: var(--ds-spacing-lg);
  background-color: var(--ds-gray-100);
  min-height: calc(100vh - 60px);
}

/* 左侧房屋树 + 右侧主内容 */
.meter-water-layout {
  display: flex;
  gap: var(--ds-spacing-md);
  align-items: flex-start;
}

.meter-water-main {
  flex: 1 1 auto;
  min-width: 0;
}

/* 房屋树面板 */
.ds-panel--tree {
  width: 280px;
  flex-shrink: 0;
  overflow: auto;
  max-height: calc(100vh - 220px);
}

/* 表格 + 分页 组成的白色面板 */
.ds-panel {
  background: #fff;
  border: 1px solid var(--ds-gray-300);
  border-radius: var(--ds-radius-md);
  box-shadow: var(--ds-shadow-sm);
  overflow: hidden;

  &__title {
    font-size: var(--ds-font-size-lg);
    font-weight: 600;
    color: var(--ds-gray-800);
  }
}
</style>
